<template>
    <crud-create 
        :open="open"
        :loading="loading"
        :title="title"
        :errorMessage="errorMessage"
        @create="create"
        @close="$emit('close')"
    >
        <devolucion-form 
            ref="form" 
            v-model="form"
            @updateProductosDevueltos="updateProductosDevueltos"
            @updateProductosNuevos="updateProductosNuevos"
            @actualizarDescuento="actualizarDescuento"
            @actualizarRecargo="actualizarRecargo"
            @actualizarMediosDePago="actualizarMediosDePago"
            @actualizarPagoRecibido="actualizarPagoRecibido"
            @actualizarListaPreciosVenta="actualizarListaPreciosVenta"
            :input-errors="inputErrors" 
        />
    </crud-create>
</template>

<script>
import MovimientoProvider from "../../../providers/MovimientoProvider";
import { CrudCreate, ClientError } from '@dracul/common-frontend'
import DevolucionForm from "../DevolucionForm/DevolucionForm.vue";

export default {
    name: "DevolucionCreate",
    components: { DevolucionForm, CrudCreate },
    props: {
        open: { type: Boolean, default: true }
    },
    data() {
        return {
            title: 'devolucion.creating',
            errorMessage: '',
            inputErrors: {},
            loading: false,
            form: {},
            productosDevueltos: [],
            productosNuevos: [],
            descuento: 0,
            mediosDePago: [],
            dineroRecibido: 0,
            cambioADevolver: 0,
            recargo: 0,
            listaPreciosVentaId: null
        }
    },
    methods: {
        async create() {
            // Validar que haya al menos un producto devuelto o uno nuevo
            if (this.productosDevueltos.length === 0 && this.productosNuevos.length === 0) {
                this.errorMessage = 'Debe agregar al menos un producto devuelto o un producto nuevo';
                return;
            }

            this.loading = true;

            try {
                // Calcular monto de devolución
                let montoDevolucion = 0;
                const productosDevueltosParaEnviar = [];

                for (const item of this.productosDevueltos) {
                    const precioBase = (item.producto.precio ?? item.producto.precioVenta) || 0;
                    const precioTotal = precioBase * item.cantidad;
                    
                    montoDevolucion += precioTotal;

                    productosDevueltosParaEnviar.push({
                        producto: item.producto.id,
                        cantidad: item.cantidad,
                        precioBase: precioBase,
                        precioTotal: precioTotal
                        // No incluir ofertaAplicada - solo es para UI
                    });
                }

                // Calcular monto de productos nuevos (estimado, el backend calculará con ofertas)
                let montoNuevosProductos = 0;
                const productosNuevosParaEnviar = [];

                for (const item of this.productosNuevos) {
                    const precioBase = (item.producto.precio ?? item.producto.precioVenta) || 0;
                    const precioTotal = precioBase * item.cantidad;
                    
                    montoNuevosProductos += precioTotal;

                    productosNuevosParaEnviar.push({
                        producto: item.producto.id,
                        cantidad: item.cantidad,
                        precioBase: precioBase,
                        precioTotal: precioTotal
                        // No incluir ofertaAplicada - solo es para UI
                    });
                }

                // Aplicar descuento y recargo
                const descuento = this.descuento != null ? parseFloat(this.descuento) : 0;
                const recargo = this.recargo != null ? parseFloat(this.recargo) : 0;

                if (descuento > 0) {
                    montoNuevosProductos = montoNuevosProductos * (1 - (descuento / 100));
                }

                if (recargo > 0) {
                    montoNuevosProductos = montoNuevosProductos * (1 + (recargo / 100));
                }

                // Calcular diferencia (puede ser positivo, negativo o cero)
                const montoAdicional = montoNuevosProductos - montoDevolucion;

                const mediosNormalizados = (this.mediosDePago || []).map((p) => ({
                    medio: p.medio || 'Efectivo',
                    monto: Number(p.monto) || 0,
                }));
                const conMonto = mediosNormalizados.filter((p) => p.monto > 0);
                let mediosDePagoInput;
                if (conMonto.length > 0) {
                    mediosDePagoInput = conMonto;
                } else if (mediosNormalizados.length === 1) {
                    mediosDePagoInput = [
                        { medio: mediosNormalizados[0].medio || 'Efectivo', monto: montoAdicional },
                    ];
                } else {
                    mediosDePagoInput = [{ medio: 'Efectivo', monto: montoAdicional }];
                }

                const input = {
                    tipo: "DEVOLUCION",
                    productosDevueltos: productosDevueltosParaEnviar,
                    itemsCarrito: productosNuevosParaEnviar,
                    precioTotal: montoAdicional,
                    descuento: descuento,
                    mediosDePago: mediosDePagoInput,
                    recargo: recargo,
                    recargoId: this.recargoId || undefined,
                    montoDevolucion: montoDevolucion,
                    montoAdicional: montoAdicional,
                    dineroRecibido: this.dineroRecibido != null ? Number(this.dineroRecibido) : undefined,
                    cambioADevolver: this.cambioADevolver != null ? Number(this.cambioADevolver) : undefined,
                    listaPreciosVentaId: this.listaPreciosVentaId || undefined,
                };

                const response = await MovimientoProvider.createDevolucion({ input });
                
                if (response) {
                    this.$emit('itemCreated', response.data.movimientoCreate);
                    this.$emit('close');
                }
            } catch (error) {
                let clientError = new ClientError(error);
                this.inputErrors = clientError.inputErrors;
                this.errorMessage = clientError.i18nMessage;
            } finally {
                this.loading = false;
            }
        },
        updateProductosDevueltos(items) {
            this.productosDevueltos = items;
        },
        updateProductosNuevos(items) {
            this.productosNuevos = items;
        },
        actualizarDescuento(descuento) {
            this.descuento = descuento;
        },
        actualizarRecargo(recargo) {
            this.recargo = recargo;
        },
        actualizarMediosDePago(mediosDePago) {
            this.mediosDePago = Array.isArray(mediosDePago) ? mediosDePago : [];
        },
        actualizarPagoRecibido(payload) {
            if (payload && typeof payload === 'object') {
                if ('dineroRecibido' in payload) this.dineroRecibido = payload.dineroRecibido;
                if ('cambioADevolver' in payload) this.cambioADevolver = payload.cambioADevolver;
            }
        },
        actualizarListaPreciosVenta(listaPreciosVentaId) {
            this.listaPreciosVentaId = listaPreciosVentaId || null;
        }
    }
}
</script>

<style scoped>
</style>
